import { capitalize } from "lodash";

import type { IconName } from "@similarweb/icons";
import Sentry from "@similarweb/sentry-logger";

import { multiIncludeExcludeType } from "app/components/multi-include-exclude-filter/consts";
import { adaptRulesForUrl } from "app/components/multi-include-exclude-filter/utils";
import type { ProjectsResponseSchema } from "app/pages/activation/types/api";
import type { EAdType } from "app/components/keyword-research/AdColumnUnit";
import { getUrlComponents } from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import { EFamilyTypes } from "app/components/dashboard/dashboard-templates/components/DashboardTemplatesTypes";
import type { IDomains, IDomainsList } from "pro-features/components/DomainsFilter/Types";
import {
    createUniqueListOfAllDomains,
    getFilteredDomain,
    transformDomainsData,
} from "pro-features/components/DomainsFilter/Utils";
import type { IDataFetcher, IDataFetcherFactory } from "app/components/widget/widget-fetchers/IDataFetcher";
import type { IWidget } from "app/components/widget/widget-types/Widget";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { ACTIVATION_USERDATA_API } from "app/pages/activation/services/constants";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import {
    adTypeColumnIconConfig,
    TABLE_COLUMN_CLICKS_SHARE,
    TEXT,
} from "app/common/keyword-research/website-search-ads/searchAdsTableConsts";
import type { IAdData, IDataRecord } from "app/common/keyword-research/website-search-ads/searchAdsTableTypes";
import fetchService from "app/common/services/salesFetchService";
import { getShareValue, getVolumeValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";
import { DefaultFetchService, requestParams } from "app/services/fetchService";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";

export type AdData = Omit<IAdData, "avgCpc" | "keywordsCount" | "position" | "key">;

export interface ITransformedAdsData {
    Data: AdData[];
    TotalCount: number;
    DomainsList?: IDomainsList[];
}
type AdTypeColumnIconConfig = Record<EAdType, { iconName: IconName; tooltip: string }>;

export const initialTableSort: ISortProps = { asc: false, sort: TABLE_COLUMN_CLICKS_SHARE };
const PARSING_ERROR_MESSAGE = "Dashboard Search Ads data fething - Error parsing multi filter JSON string:";

const searchAdsApiConfigs = {
    Website: "/api/SearchAds/Website/Table",
    Keyword: "/api/SearchAds/Keyword/Table",
};

export const getDomains = async (api: string, params): Promise<IDomainsList[]> => {
    try {
        const domains: IDomains[] = await fetchService.post(`${api}?${params}`);
        return transformDomainsData(domains);
    } catch (err) {
        Sentry.error(`Error fetching domains`, err);
    }
};

export const adaptMultiIncludeExcludeRulesForUrl = async (
    filter: string,
    type: (typeof multiIncludeExcludeType)[keyof typeof multiIncludeExcludeType],
    message?: string,
): Promise<string> => {
    const parsedFilter = safeJsonParse(filter, [], message);
    const adaptedRules = await adaptRulesForUrl(parsedFilter, type);
    return JSON.stringify(adaptedRules);
};

class SearchAdsDataFetcher<T> implements IDataFetcher<T> {
    protected widget: IWidget<T>;

    constructor(widget) {
        this.widget = widget;
    }

    async fetch(apiParams = this.widget.apiParams) {
        const fetchService = DefaultFetchService.getInstance();
        const activationSets =
            (await DefaultFetchService.getInstance().get<ProjectsResponseSchema | null>(ACTIVATION_USERDATA_API))
                ?.activationSets ?? [];

        const isWebsiteFamily = this.widget._metricConfig.family === capitalize(EFamilyTypes.Website);
        const api = searchAdsApiConfigs[this.widget._metricConfig.family];
        const {
            multiIncludeExcludeKeywords,
            multiIncludeExcludeUrl,
            multiIncludeExcludeTitle,
            pageFilter,
            adType,
            key,
            keys,
            from,
            to,
            country,
            isDaily,
            isWindow,
            webSource,
            orderBy,
            selectedCategory,
            domains,
        } = apiParams;

        const [sort] = orderBy?.split?.(" ") ?? [initialTableSort.sort];
        const keyForApi = key ?? keys;

        const allDomains = createUniqueListOfAllDomains(domains?.split(","), activationSets);

        const serverParams = {
            webSource,
            isDaily,
            isWindow,
            country,
            from,
            to,
            keys: keyForApi,
            adType: adType ?? TEXT,
            sort,
            asc: false,
            selectedCategory,
            DomainsFilter: allDomains,
            ...(!isWebsiteFamily ? { device: webSource } : {}),
            ...(isWebsiteFamily
                ? {
                      pageFilterJson:
                          pageFilter ??
                          JSON.stringify(
                              keyForApi.split(",").map((url) => {
                                  const { subdomain } = getUrlComponents(url);
                                  return { url, searchType: subdomain ? "subdomain" : "domain" };
                              }),
                          ),
                  }
                : {}),
        };

        const paramsAsString = requestParams(serverParams);
        const { selectedCategory: category, ...rest } = serverParams;
        const paramsAsStringForDomains = requestParams({
            ...rest,
            category,
        });
        const domainsItems = this.widget._metricConfig.domainsEndpoint
            ? await getDomains(this.widget._metricConfig.domainsEndpoint, paramsAsStringForDomains)
            : [];
        const domainsList = getFilteredDomain(domainsItems, activationSets);

        return Promise.all([
            adaptMultiIncludeExcludeRulesForUrl(
                multiIncludeExcludeKeywords,
                multiIncludeExcludeType.KEYWORD,
                PARSING_ERROR_MESSAGE,
            ),
            adaptMultiIncludeExcludeRulesForUrl(
                multiIncludeExcludeUrl,
                multiIncludeExcludeType.URL,
                PARSING_ERROR_MESSAGE,
            ),
            adaptMultiIncludeExcludeRulesForUrl(
                multiIncludeExcludeTitle,
                multiIncludeExcludeType.TITLE,
                PARSING_ERROR_MESSAGE,
            ),
        ])
            .then((rulesAsStrings) => {
                const allRules = rulesAsStrings
                    .map((ruleString) => JSON.parse(ruleString))
                    .filter((rules) => rules.length > 0)
                    .flat();
                return fetchService.post<IDataRecord>(`${api}?${paramsAsString}`, allRules);
            })
            .then((res) => (res.Data?.length ? this.transformData(res, domainsList) : { Data: [] }) as T)
            .catch((e) => {
                Sentry.error("Dashboard Search Ads data fething - Error fetching data:", e);
                throw e;
            });
    }

    destroy() {
        return undefined;
    }

    async transformData(response: IDataRecord, domainsList: IDomainsList[]): Promise<ITransformedAdsData> {
        const transformedData = response.Data.map((record) => {
            if (!record.SerpType) {
                return;
            }
            const {
                Clicks,
                ClicksShare,
                SerpType,
                LandingPage,
                TopKeyword,
                Volume,
                DestUrl,
                Image,
                ClicksChange,
                Domain,
                Favicon,
                ...rest
            } = record;
            const { iconName, tooltip } = adTypeColumnIconConfig[SerpType] as AdTypeColumnIconConfig[EAdType];
            const totalClicks = getVolumeValue(Clicks);
            return {
                clicksShare: { totalClicks, value: getShareValue(ClicksShare) },
                clicksChange: ClicksChange,
                adDetails: {
                    value: { ...rest, ImageUrl: Image, DisplayUrl: DestUrl, AdType: SerpType, DestUrl: LandingPage },
                },
                adType: { iconDetails: { iconName, tooltip: translate(tooltip) } },
                volume: abbrNumberFilter()(Volume),
                topKeyword: TopKeyword,
                landingPage: LandingPage,
                domain: {
                    value: Domain,
                    favicon: Favicon,
                },
            };
        }).filter(Boolean);
        return { Data: transformedData, TotalCount: response.TotalCount, DomainsList: domainsList };
    }
}

const SearchAdsDataFetcherFactory: IDataFetcherFactory<IDataRecord> = {
    create: function (widget) {
        return new SearchAdsDataFetcher<IDataRecord>(widget);
    },
};

export default SearchAdsDataFetcherFactory;
